import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import { useAuth } from 'contexts/AuthContext';
import LocationSelector from './components/LocationSelector';
import LegalMatterSelector from './components/LegalMatterSelector';

const ClientProfileSetup = () => {
  const navigate = useNavigate();
  const { user, profile, updateProfile } = useAuth();
  const [formData, setFormData] = useState({
    phone: profile?.phone || '',
    location: '',
    legalMatterCategory: '',
    legalMatterDescription: ''
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentStep, setCurrentStep] = useState(1);
  const [isExiting, setIsExiting] = useState(false);
  const totalSteps = 3;

  // Auto-populate email from authenticated user
  const userEmail = user?.email || '';

  useEffect(() => {
    if (profile?.profile_complete) {
      navigate('/client-portal');
    }
  }, [profile, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (validationErrors?.[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleLocationChange = (location) => {
    setFormData(prev => ({
      ...prev,
      location
    }));
    if (validationErrors?.location) {
      setValidationErrors(prev => ({
        ...prev,
        location: ''
      }));
    }
  };

  const handleLegalMatterChange = (category) => {
    setFormData(prev => ({
      ...prev,
      legalMatterCategory: category
    }));
    if (validationErrors?.legalMatterCategory) {
      setValidationErrors(prev => ({
        ...prev,
        legalMatterCategory: ''
      }));
    }
  };

  const validateStep = (step) => {
    const errors = {};

    if (step === 1) {
      if (!formData?.phone) {
        errors.phone = 'Phone number is required';
      } else if (!/^(\+254|0)[17]\d{8}$/?.test(formData?.phone?.replace(/\s/g, ''))) {
        errors.phone = 'Please enter a valid Kenyan phone number';
      }
    }

    if (step === 2) {
      // County is required — city/town is optional
      if (!formData?.location) {
        errors.location = 'Please select your county';
      }
    }

    if (step === 3) {
      if (!formData?.legalMatterCategory) {
        errors.legalMatterCategory = 'Please select a legal matter category';
      }
      if (!formData?.legalMatterDescription?.trim()) {
        errors.legalMatterDescription = 'Please provide a brief description';
      } else if (formData?.legalMatterDescription?.trim()?.length < 20) {
        errors.legalMatterDescription = 'Description must be at least 20 characters';
      } else if (formData?.legalMatterDescription?.trim()?.length > 500) {
        errors.legalMatterDescription = 'Description must not exceed 500 characters';
      }
    }

    setValidationErrors(errors);
    return Object?.keys(errors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const navigateWithTransition = (path, state = {}) => {
    setIsExiting(true);
    setTimeout(() => {
      navigate(path, { state });
    }, 500);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateStep(currentStep)) return;

    setIsLoading(true);
    setError('');

    try {
      await updateProfile({
        email: userEmail,
        phone: formData?.phone,
        location: formData?.location,
        legal_matter_category: formData?.legalMatterCategory,
        legal_matter_description: formData?.legalMatterDescription,
        profile_complete: true
      });

      // Smooth transition to client portal with setup flag
      navigateWithTransition('/client-portal', { fromSetup: true });
    } catch (err) {
      console.error('Profile setup error:', err);
      setError(err?.message || 'Failed to complete profile setup. Please try again.');
      setIsLoading(false);
    }
  };

  const handleSkip = () => {
    navigateWithTransition('/client-portal');
  };

  const getProgressPercentage = () => {
    return (currentStep / totalSteps) * 100;
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-secondary-50 flex items-center justify-center p-4"
      style={{
        transition: 'opacity 0.5s ease, transform 0.5s ease',
        opacity: isExiting ? 0 : 1,
        transform: isExiting ? 'translateY(-20px)' : 'translateY(0)'
      }}
    >
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary rounded-lg flex items-center justify-center mx-auto mb-4">
            <Icon name="UserCheck" size={32} color="white" />
          </div>
          <h1 className="text-3xl font-heading font-bold text-text-primary mb-2">Complete Your Profile</h1>
          <p className="text-text-secondary">Help us understand your legal needs better</p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-text-primary">Step {currentStep} of {totalSteps}</span>
            <span className="text-sm text-text-secondary">{Math.round(getProgressPercentage())}% Complete</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${getProgressPercentage()}%` }}
            />
          </div>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {error && (
            <div className="bg-error-50 border border-error-100 text-error rounded-lg p-3 mb-6 text-sm flex items-start">
              <Icon name="AlertCircle" size={20} className="mr-2 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Step 1: Contact Information */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <Icon name="Phone" size={48} className="text-primary mx-auto mb-3" />
                  <h2 className="text-xl font-semibold text-text-primary mb-2">Contact Information</h2>
                  <p className="text-sm text-text-secondary">We'll use this to reach you about your case</p>
                </div>

                {/* Auto-populated read-only email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-2">
                    Email Address
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-success-100 text-success-700">
                      <Icon name="CheckCircle" size={12} className="mr-1" />
                      Verified
                    </span>
                  </label>
                  <div className="relative">
                    <input
                      type="email"
                      id="email"
                      value={userEmail}
                      readOnly
                      className="w-full px-4 py-3 border border-border rounded-lg bg-secondary-50 text-text-secondary cursor-not-allowed focus:outline-none"
                    />
                    <Icon name="Lock" size={20} className="absolute right-3 top-3.5 text-text-secondary" />
                  </div>
                  <p className="text-xs text-text-secondary mt-1">This email is linked to your verified account and cannot be changed here</p>
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-text-primary mb-2">
                    Phone Number *
                  </label>
                  <div className="relative">
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData?.phone}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth ${
                        validationErrors?.phone ? 'border-error' : 'border-border'
                      }`}
                      placeholder="+254 712 345 678 or 0712 345 678"
                    />
                    <Icon name="Phone" size={20} className="absolute right-3 top-3.5 text-text-secondary" />
                  </div>
                  {validationErrors?.phone && (
                    <p className="text-error text-xs mt-1">{validationErrors?.phone}</p>
                  )}
                  <p className="text-xs text-text-secondary mt-1">Enter a valid Kenyan phone number</p>
                </div>
              </div>
            )}

            {/* Step 2: Location */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <Icon name="MapPin" size={48} className="text-primary mx-auto mb-3" />
                  <h2 className="text-xl font-semibold text-text-primary mb-2">Your Location</h2>
                  <p className="text-sm text-text-secondary">This helps us match you with local legal experts</p>
                </div>

                <LocationSelector
                  value={formData?.location}
                  onChange={handleLocationChange}
                  error={validationErrors?.location}
                />
              </div>
            )}

            {/* Step 3: Legal Matter */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <Icon name="Scale" size={48} className="text-primary mx-auto mb-3" />
                  <h2 className="text-xl font-semibold text-text-primary mb-2">Legal Matter Details</h2>
                  <p className="text-sm text-text-secondary">Tell us about your legal needs</p>
                </div>

                <LegalMatterSelector
                  value={formData?.legalMatterCategory}
                  onChange={handleLegalMatterChange}
                  error={validationErrors?.legalMatterCategory}
                />

                <div>
                  <label htmlFor="legalMatterDescription" className="block text-sm font-medium text-text-primary mb-2">
                    Brief Description *
                  </label>
                  <textarea
                    id="legalMatterDescription"
                    name="legalMatterDescription"
                    value={formData?.legalMatterDescription}
                    onChange={handleInputChange}
                    rows={5}
                    maxLength={500}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth resize-none ${
                      validationErrors?.legalMatterDescription ? 'border-error' : 'border-border'
                    }`}
                    placeholder="Please describe your legal situation briefly. Include key details that will help us understand your needs..."
                  />
                  <div className="flex justify-between items-center mt-1">
                    {validationErrors?.legalMatterDescription ? (
                      <p className="text-error text-xs">{validationErrors?.legalMatterDescription}</p>
                    ) : (
                      <p className="text-xs text-text-secondary">Minimum 20 characters</p>
                    )}
                    <span className="text-xs text-text-secondary">
                      {formData?.legalMatterDescription?.length || 0}/500
                    </span>
                  </div>
                </div>

                {/* Privacy Message */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="Lock" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-blue-900 mb-1">Your Privacy Matters</p>
                      <p className="text-xs text-blue-700">
                        All information you provide is encrypted and protected. We only share details with verified legal professionals assigned to your case.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between pt-6 border-t border-border">
              <div>
                {currentStep > 1 && (
                  <button
                    type="button"
                    onClick={handleBack}
                    className="flex items-center space-x-2 text-text-secondary hover:text-text-primary transition-smooth"
                  >
                    <Icon name="ChevronLeft" size={20} />
                    <span>Back</span>
                  </button>
                )}
              </div>

              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={handleSkip}
                  className="text-text-secondary hover:text-text-primary transition-smooth text-sm"
                >
                  Skip for now
                </button>

                {currentStep < totalSteps ? (
                  <button
                    type="button"
                    onClick={handleNext}
                    className="bg-primary text-white py-3 px-6 rounded-lg font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-smooth flex items-center space-x-2"
                  >
                    <span>Next</span>
                    <Icon name="ChevronRight" size={20} />
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="bg-primary text-white py-3 px-6 rounded-lg font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-smooth flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                        <span>Completing...</span>
                      </>
                    ) : (
                      <>
                        <Icon name="Check" size={20} />
                        <span>Complete Setup</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ClientProfileSetup;